import { createContext, useState, useEffect, useCallback, useMemo } from 'react';

export const CartContext = createContext();

const CART_STORAGE_KEY = 'simplyflora_cart';
const CART_EXPIRY_TIME = 60 * 60 * 1000; // 1 hour in milliseconds

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [cartExpired, setCartExpired] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        const { items, timestamp } = parsedCart;

        // Check if cart has expired
        if (Date.now() - timestamp > CART_EXPIRY_TIME) {
          localStorage.removeItem(CART_STORAGE_KEY);
          setCartExpired(true);
          setCart([]);
        } else {
          setCart(items || []);
        }
      } catch (error) {
        console.error('Failed to load cart:', error);
        setCart([]);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cart.length > 0) {
      const cartData = {
        items: cart,
        timestamp: Date.now(),
      };
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
    } else {
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  }, [cart]);

  const addToCart = useCallback((flower, quantity = 1) => {
    setCartExpired(false);
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === flower.id);

      if (existingItem) {
        return prevCart.map((item) =>
          item.id === flower.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [
        ...prevCart,
        {
          id: flower.id,
          name: flower.name,
          price: flower.price,
          discountPercentage: flower.discountPercentage || null,
          discountedPrice: flower.discountedPrice || null,
          imageFilename: flower.imageFilename,
          quantity,
          addedAt: Date.now(),
          customMessage: '',
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback((flowerId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(flowerId);
      return;
    }

    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === flowerId ? { ...item, quantity } : item
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeFromCart = useCallback((flowerId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== flowerId));
  }, []);

  const updateCustomMessage = useCallback((flowerId, message) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === flowerId ? { ...item, customMessage: message } : item
      )
    );
  }, []);

  const updateCartItem = useCallback((flowerId, updates) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === flowerId ? { ...item, ...updates } : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  }, []);

  const getCartTotal = useCallback(() => {
    return cart.reduce((total, item) => {
      // Use discountedPrice if available, otherwise use regular price
      const effectivePrice = item.discountedPrice || item.price;
      return total + effectivePrice * item.quantity;
    }, 0);
  }, [cart]);

  const getCartItemCount = useCallback(() => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  }, [cart]);

  const value = useMemo(() => ({
    cart,
    cartExpired,
    setCartExpired,
    addToCart,
    updateQuantity,
    removeFromCart,
    updateCustomMessage,
    updateCartItem,
    clearCart,
    getCartTotal,
    getCartItemCount,
  }), [
    cart,
    cartExpired,
    setCartExpired,
    addToCart,
    updateQuantity,
    removeFromCart,
    updateCustomMessage,
    updateCartItem,
    clearCart,
    getCartTotal,
    getCartItemCount
  ]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
